import { Button, Input, Select, Space, Switch, Tooltip } from "antd";
import { TableProps } from "antd/es/table";
import { useEffect } from "react";
import { useAppSelector } from "../../../app/store";
import AddIcon from "../../../assets/icons/AddIcon";
import EditIcon from "../../../assets/icons/EditIcon";
import SearchIcon from "../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../assets/icons/SelectDownIcon";
import CustomTable from "../../../components/ui/CustomTable";
import {
  useGetExpenseUnitsQuery,
  useUpdateExpenseUnitStatusMutation,
} from "../../../features/org-setting/expenseUnit/expenseUnitApi";
import useDebounce from "../../../hooks/useDebounce";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import useTable from "../../../hooks/useTable";
import SidebarLayout from "../../../layouts/SidebarLayout";
import { formatDate } from "../../../lib/time";
import { toast } from "../../../lib/toast";
import {
  ExpenseUnitData,
  GetExpenseUnitsParams,
} from "../../../types/org-setting/expenseUnit";
import { countParPageSerialNumber } from "../../../utils/number";
import {
  addKeyInArray,
  selectSearchOption,
  skipData,
} from "../../../utils/utils";
import ManageExpenseUnit from "./_components/ManageExpenseUnit";

enum ExpenseUnitStatus {
  "Inactive",
  "Active",
}

export default function ExpenseUnit() {
  const { open, handleClose, handleOpen } = useModal();
  const { keyword, handleInputChange } = useDebounce();
  const { user } = useAppSelector((state) => state.auth);
  const {
    page,
    limit,
    filter,
    handlePagination,
    handleLimit,
    resetPagination,
    handleFilter,
  } = useTable<GetExpenseUnitsParams>();
  const [
    updateExpenseUnitStatus,
    {
      isSuccess: isStatusUpdateSuccess,
      isError: isStatusUpdateError,
      error: statusUpdateError,
    },
  ] = useUpdateExpenseUnitStatusMutation();
  const { isLoading, isError, error, data } = useGetExpenseUnitsQuery({
    params: {
      ...filter,
      limit,
      skip: skipData(page, limit),
      ...(keyword ? { search: keyword } : {}),
      company_id: user?.data?.company_id,
    },
  });

  useError(isError, error, "Get business unit data fail");
  useError(
    isStatusUpdateError,
    statusUpdateError,
    "Business unit status update fail"
  );

  const columns: TableProps<ExpenseUnitData>["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: "Business Unit",
      dataIndex: "name",
      render: (val, record) => {
        return (
          <div className="flex items-center gap-1.5">
            <img
              className="w-6 h-6 rounded-full"
              src={record?.logo || "/demo.png"}
              alt=""
            />

            <h6>{val}</h6>
          </div>
        );
      },
    },
    {
      title: "Head of Business",
      dataIndex: "company",
      render: (val) => {
        return (
          <div className="flex items-center gap-1.5">
            <img
              className="w-6 h-6 rounded-full"
              src={val?.logo || "/demo.png"}
              alt=""
            />

            <h6>{val?.company_name}</h6>
          </div>
        );
      },
    },

    // {
    //   title: "Total Employees",
    //   dataIndex: "employee_count",
    //   render: (val) => val ?? 0,
    // },
    {
      title: "Created at",
      dataIndex: "created_at",
      render: (val) => (val ? formatDate(val) : "-"),
    },
    {
      title: "Updated at",
      dataIndex: "updated_at",
      render: (val) => (val ? formatDate(val) : "-"),
    },
    {
      title: "Status",
      dataIndex: "is_published",
      // render: (val) => (
      //   <span
      //     className={
      //       ExpenseUnitStatus[val] ? "status-success" : "status-danger"
      //     }
      //   >
      //     {ExpenseUnitStatus[val]}
      //   </span>
      // ),
      render: (val, record) => (
        <Space size={4}>
          <Switch
            className="custom-switch"
            checked={val === 1}
            onChange={() =>
              updateExpenseUnitStatus({
                id: record?.id,
                body: {
                  is_published: val === 0 ? 1 : 0,
                },
              })
            }
            // disabled={isStatusUpdateLoading}
          />
          <span className="text-header text-sm font-medium">
            {ExpenseUnitStatus[val]}
          </span>
        </Space>
      ),
    },
    {
      title: "Action",
      width: 100,
      render: (val) => (
        <Space size={8}>
          <Tooltip title="Edit Business Unit">
            <Button
              className="action-button"
              size="small"
              icon={<EditIcon />}
              onClick={() =>
                handleOpen({
                  type: "UPDATE",
                  data: val?.id,
                })
              }
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // success toast message
  useEffect(() => {
    if (isStatusUpdateSuccess) {
      toast("success", "Status updated successfully");
    }
  }, [isStatusUpdateSuccess]);

  return (
    <SidebarLayout title="Business Unit List">
      <CustomTable
        loading={isLoading}
        limit={limit}
        page={page}
        columns={columns}
        total={data?.data?.totalLength}
        dataSource={addKeyInArray(data?.data?.businessUnits)}
        handleLimit={handleLimit}
        handlePagination={handlePagination}
        filterSection={
          <>
            <Input
              suffix={<SearchIcon />}
              placeholder="Search by name"
              onChange={(val) => {
                handleInputChange(val);
                resetPagination();
              }}
            />
            <Select
              allowClear
              showSearch
              placeholder="Status"
              filterOption={selectSearchOption}
              options={[
                { label: "Active", value: 1 },
                { label: "Inactive", value: 0 },
              ]}
              onChange={(value) => handleFilter("is_published", value)}
              className="min-w-[90px]"
              suffixIcon={<SelectDownIcon />}
              rootClassName="icon-gray"
            />
            <Button
              icon={<AddIcon />}
              type="primary"
              className="sm-button"
              onClick={() =>
                handleOpen({
                  type: "ADD",
                })
              }
            >
              Add Business Unit
            </Button>
          </>
        }
      />

      {(open.type === "ADD" || open.type === "UPDATE") && (
        <ManageExpenseUnit
          open={open.open}
          type={open.type}
          data={open.data}
          handleClose={handleClose}
        />
      )}
    </SidebarLayout>
  );
}
